import type { IrradiationKind } from './supabase/queries';

// Shared strain-card presentation helpers. Used by /strains, /stock and
// /watchlist so the three surfaces render strain names, THC suffixes and
// stock-derived variants identically.

export type StockStatus = 'In Stock' | 'Low Stock' | 'Out of Stock';

// The minimal shape every stock-bearing row exposes. LiveStockEntry
// (from queries.ts) and the raw stock_status rows the watchlist fetches
// both satisfy this, so summariseStock works against either.
export type StockLike = {
  status: StockStatus;
  thc_pct_advertised: number | null;
  thc_pct_is_upper_bound: boolean;
  cbd_pct_advertised: number | null;
  cbd_pct_is_upper_bound: boolean;
  irradiation: IrradiationKind | null;
  price_per_gram_gbp: number | null;
  pharmacy_slug?: string | null;
};

// A strain's stock rolled up into one card's worth of data. Since
// migration 0048 made potency part of strain identity, a strain_id is a
// single product at a single potency — so we collapse its rows (one per
// pharmacy) into a single summary rather than splitting by THC.
export type StockSummary = {
  thc: number | null;
  thcUpper: boolean;
  cbd: number | null;
  cbdUpper: boolean;
  irradiations: IrradiationKind[];
  // Best status across the pharmacies that stock this strain.
  status: StockStatus;
  inStock: boolean;
  minPrice: number | null;
  hasPriceVariation: boolean;
  pharmacyCount: number;
};

const STATUS_RANK: Record<StockStatus, number> = {
  'In Stock': 0,
  'Low Stock': 1,
  'Out of Stock': 2,
};

/**
 * The "T28" name suffix derived from an advertised THC percentage.
 * Rounds to the nearest whole number; returns null when THC is unknown
 * so callers can omit the suffix entirely.
 */
export function thcSuffix(thc: number | null | undefined): string | null {
  if (thc == null || !Number.isFinite(thc)) return null;
  return `T${Math.round(thc)}`;
}

/** A strain's display name with its THC suffix appended when available. */
export function strainNameWithThc(
  name: string,
  thc: number | null | undefined,
): string {
  const suffix = thcSuffix(thc);
  return suffix ? `${name} ${suffix}` : name;
}

/**
 * Roll a strain's stock rows (one per pharmacy) up into a single summary:
 * best status + cheapest price + the union of reported irradiation
 * methods, with THC/CBD taken from the representative row. Returns null
 * when the strain has no stock data.
 */
export function summariseStock(rows: StockLike[]): StockSummary | null {
  if (rows.length === 0) return null;
  const rep = [...rows].sort((a, b) => {
    const s = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (s !== 0) return s;
    const pa = a.price_per_gram_gbp ?? Number.POSITIVE_INFINITY;
    const pb = b.price_per_gram_gbp ?? Number.POSITIVE_INFINITY;
    return pa - pb;
  })[0]!;
  const prices = rows
    .map((r) => r.price_per_gram_gbp)
    .filter((p): p is number => p != null);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxPrice = prices.length ? Math.max(...prices) : null;
  const pharmacies = new Set(
    rows.map((r) => r.pharmacy_slug).filter((s): s is string => !!s),
  );
  const irradiations = Array.from(
    new Set(
      rows.map((r) => r.irradiation).filter((v): v is IrradiationKind => v != null),
    ),
  );
  return {
    thc: rep.thc_pct_advertised,
    thcUpper: rep.thc_pct_is_upper_bound,
    cbd: rep.cbd_pct_advertised,
    cbdUpper: rep.cbd_pct_is_upper_bound,
    irradiations,
    status: rep.status,
    inStock: rep.status === 'In Stock' || rep.status === 'Low Stock',
    minPrice,
    hasPriceVariation: minPrice != null && maxPrice != null && minPrice !== maxPrice,
    pharmacyCount: pharmacies.size,
  };
}
